import { gql } from '@apollo/client';

/**
 * Spec 015: unlisting, recall checks and the admin relist. Written out in full
 * (no `${}` fragments) so codegen generates the hooks.
 */

export const CAR_LISTING_STATUS = gql`
  query CarListingStatus($carId: ID!) {
    carListingStatus(carId: $carId) {
      carId
      canHostRelist
      isAdminView
      supportEmail
      hold {
        id
        reason
        actorType
        actorName
        note
        createdAt
        pendingManualCheck
        campaigns {
          campaignNumber
          component
          summary
          consequence
          remedy
          remedyAvailable
          reportReceivedDate
          parkIt
          parkOutSide
          overTheAirUpdate
          nhtsaUrl
        }
      }
    }
  }
`;

export const ADMIN_UNLIST_CAR = gql`
  mutation AdminUnlistCar($carId: ID!, $note: String!) {
    adminUnlistCar(carId: $carId, note: $note) {
      id
      status
    }
  }
`;

export const ADMIN_RELIST_CAR = gql`
  mutation AdminRelistCar($input: AdminRelistCarInput!) {
    adminRelistCar(input: $input) {
      id
      status
      recallCheckedAt
    }
  }
`;

export const CREATE_PRESIGNED_URL_FOR_RECALL_EVIDENCE = gql`
  mutation CreatePresignedUrlForRecallEvidence(
    $carId: ID!
    $fileType: String!
    $contentLength: Int!
  ) {
    createPresignedUrlForRecallEvidence(
      carId: $carId
      fileType: $fileType
      contentLength: $contentLength
    ) {
      url
      key
    }
  }
`;

export const RECORD_RECALL_CHECK = gql`
  mutation RecordRecallCheck($input: RecordRecallCheckInput!) {
    recordRecallCheck(input: $input) {
      id
      checkedOn
      result
      source
    }
  }
`;

export const CAR_RECALL_HISTORY = gql`
  query CarRecallHistory($carId: ID!) {
    carRecallChecks(carId: $carId) {
      id
      checkedOn
      result
      source
      note
      createdAt
    }
    carRecallClearances(carId: $carId) {
      campaignNumber
      clearedAt
      clearedByName
      evidenceUrls
      note
    }
  }
`;

export const UNLISTED_CARS = gql`
  query UnlistedCars($filter: UnlistedCarsFilter) {
    unlistedCars(filter: $filter) {
      total
      items {
        hostName
        hostEmail
        futureTripCount
        needsAction
        car {
          id
          make
          model
          year
          vin
          name
          thumbnailUrl
        }
        hold {
          id
          reason
          actorType
          actorName
          note
          createdAt
          pendingManualCheck
          campaigns {
            campaignNumber
            component
            summary
            remedy
            remedyAvailable
            parkIt
            parkOutSide
            nhtsaUrl
          }
        }
      }
    }
  }
`;

export const CARS_PENDING_RECALL_CHECK = gql`
  query CarsPendingRecallCheck($limit: Int!, $offset: Int!) {
    carsPendingRecallCheck(limit: $limit, offset: $offset) {
      id
      make
      model
      year
      vin
      name
      createdAt
      owner {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const CARS_WITHOUT_RECALL_MATCH = gql`
  query CarsWithoutRecallMatch {
    carsWithoutRecallMatch {
      reason
      car {
        id
        make
        model
        year
        vin
        name
        owner {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export const RECALL_JOB_STATUS = gql`
  query RecallJobStatus {
    recallJobStatus {
      mode
      lastRunAt
      lastSuccessAt
      lastError
      modelYearsTracked
      modelYearsFailing
      unmatchedCarCount
    }
  }
`;

export const ADMIN_CANCEL_BOOKING_FOR_UNLISTED_CAR = gql`
  mutation AdminCancelBookingForUnlistedCar($bookingId: ID!, $note: String) {
    adminCancelBookingForUnlistedCar(bookingId: $bookingId, note: $note) {
      id
      status
      cancellationReason
    }
  }
`;

export const ADMIN_END_TRIP_EARLY_FOR_RECALL = gql`
  mutation AdminEndTripEarlyForRecall($bookingId: ID!, $endAt: Float!, $note: String) {
    adminEndTripEarlyForRecall(bookingId: $bookingId, endAt: $endAt, note: $note) {
      id
      status
      endAt
    }
  }
`;

/** Spec 015 US-8: the car's trips on this site, for the admin trip actions. */
export const UNLISTED_CAR_TRIPS = gql`
  query UnlistedCarTrips($carId: String!) {
    carBookings(carId: $carId) {
      id
      status
      startAt
      endAt
      brandSite
      hostFinishedCheckin
      guestFinishedCheckin
      hostFinishedCheckout
      guest {
        id
        firstName
        lastName
        email
      }
    }
  }
`;
